import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FadeUp, SectionHead } from "@/components/Reveal";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/lib/i18n";

const FALLBACK = [
  { title_en: "Concert & Music Festival Production", title_zh: "演唱会 / 音乐节制作", description: "从策划、制作到执行的一站式演唱会与音乐节制作服务。" },
  { title_en: "Artist Management", title_zh: "艺人管理", description: "亚洲及国际艺人在亚洲与全球市场的代理、管理与巡演统筹。" },
  { title_en: "Music Production", title_zh: "音乐制作", description: "词曲创作与录音、专辑与 MV 制作、影视音乐合作。" },
  { title_en: "Corporate Launching Events", title_zh: "商业发布活动", description: "品牌发布会、企业年会与商业演出策划执行。" },
  { title_en: "Nightlife & Club Events", title_zh: "夜店活动", description: "俱乐部主题活动与国际 DJ 阵容策划。" },
  { title_en: "Event Planning & Execution", title_zh: "活动策划与执行", description: "活动全程策划、统筹与现场执行。" },
  { title_en: "Hospitality Management", title_zh: "接待管理", description: "交通安排、后台 Rider 与住宿 Rider 统筹。" },
  { title_en: "On Ground / Logistic Management", title_zh: "现场及后勤管理", description: "安保、检票、艺人保镖、制作与技术团队、迎宾、医疗团队、机场 VIP 服务、制作物流、警力协调及 PUSPAL 申请服务。" },
  { title_en: "Technical Production", title_zh: "技术制作", description: "制作团队统筹、Show Director / Producer 及舞台技术执行。" },
  { title_en: "Consultation", title_zh: "咨询服务", description: "演艺项目与娱乐产业专业咨询。" },
];

export default function Services() {
  const { lang } = useLang();
  const zh = lang === "zh";
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.get("/services").then((r) => r.data),
    staleTime: 60_000,
  });
  const list = services.length ? services : FALLBACK;

  return (
    <section id="services" data-testid="services" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <SectionHead no="03" en="WHAT WE DO" zh="服务项目" />
        <div className="border-t border-white/10">
          {list.map((s, i) => (
            <FadeUp key={s.id || i} delay={Math.min(i * 0.04, 0.3)}>
              <div className="group grid grid-cols-12 gap-4 items-start py-7 md:py-9 border-b border-white/10 px-2 md:px-4 hover:bg-[#ffde00] transition-colors duration-300 cursor-default" data-testid={`service-row-${i}`}>
                <div className="col-span-2 md:col-span-1 font-mono2 text-xs md:text-sm text-[#ffde00] group-hover:text-black pt-2 transition-colors duration-300">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <h3 className="font-display text-2xl md:text-4xl uppercase text-[#f5f5f5] group-hover:text-black leading-none transition-colors duration-300">
                    {zh ? s.title_zh || s.title_en : s.title_en}
                  </h3>
                  <p className="font-mono2 text-[10px] text-[#52525b] group-hover:text-black/60 tracking-[0.2em] mt-2 uppercase transition-colors duration-300">
                    {zh ? s.title_en : s.title_zh}
                  </p>
                </div>
                <p className="col-span-10 col-start-3 md:col-span-6 md:col-start-6 font-zh text-sm md:text-base text-[#a1a1aa] group-hover:text-black/80 leading-relaxed transition-colors duration-300">
                  {zh ? s.description : s.description_en || s.description}
                </p>
                <ArrowRight
                  size={20}
                  className="hidden md:block col-span-1 justify-self-end self-center text-[#ffde00] group-hover:text-black group-hover:translate-x-2 transition-all duration-300"
                />
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-14 text-center">
          <p className="font-zh text-[#a1a1aa] mb-6">{zh ? "需要为您的下一场演出或活动定制方案？" : "Need a tailored plan for your next show or event?"}</p>
          <Link to="/contact" data-testid="services-cta" className="btn-yellow">
            {zh ? "与我们洽谈" : "TALK TO US"}
            <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}
